import {Injectable, Injector} from "@angular/core";
import {NavigationService} from "../orchestration/navigation.service";
import {ButtonConnotation} from "../buttons/button/button";
import {BehaviorSubject, Subject} from "rxjs";
import {MessageDetails, MessageLevel} from "./message/message";
import {DataClientErrorMessage, ErrorService, ServerLoggingService} from "@fedelta-pos/data-client";
import {CheckboxSpec} from "./simple-dialog-page/simple-dialog-page.component";

/**
 * Service to show notifications
 */
@Injectable()
export class NotificationService {
    private static readonly FOOTER_MESSAGE_DEFAULT_TIMEOUT_SECONDS: number = 5;

    public alertDisplayed$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public footerMessages: MessageDetails[] = [];
    public footerMessagesChanged$: Subject<void> = new Subject<void>();

    constructor(private injector: Injector,
                private errorService: ErrorService,
                private serverLoggingService: ServerLoggingService,
                private navigationService: NavigationService) {
        console.log("--- NotificationService");

        this.errorService.dataClientErrors.subscribe(
            (errorMessage: DataClientErrorMessage) => {
                if (errorMessage.displayable) {
                    console.log("NotificationService: data client error: " + errorMessage.message);
                    this.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "'" + errorMessage.message + "'"));
                }
            });

        this.errorService.errors.subscribe((errorMessage: any) => console.error("ErrorHandlerService: unhandled exception: " + errorMessage));
    }

    displayFooterMessage(message: MessageDetails, timeoutSeconds: number = NotificationService.FOOTER_MESSAGE_DEFAULT_TIMEOUT_SECONDS) {
        this.footerMessages.unshift(message);
        this.footerMessagesChanged$.next();
        setTimeout(() => {
            message.closed = true;
            this.footerMessagesChanged$.next();
            setTimeout(() => {
                this.footerMessages = this.footerMessages.filter((m: MessageDetails) => m !== message);
                this.footerMessagesChanged$.next();
            }, 1000);
        }, timeoutSeconds * 1000);
    }

    async displayAlert(title: string, message: string, translationParams?: Object, cancelResult: boolean = false): Promise<any> {
        this.alertDisplayed$.next(true);
        const result: any = await this.navigationService.createPage("woa-simple-dialog-page", {
            showHeader: !!title,
            showCloseButton: false,
            title: title,
            contentText: message,
            translationParams: translationParams,
            buttons: [{label: "woa.button.common.ok"}]
        });
        this.alertDisplayed$.next(false);
        return result === undefined ? cancelResult : result;
    }

    async displayAlertWithCheckbox(title: string, message: string, checkboxLabel: string, translationParams?: Object, cancelResult: boolean = false): Promise<boolean> {
        this.alertDisplayed$.next(true);
        const result: any = await this.navigationService.createPage("woa-simple-dialog-page", {
            showCloseButton: false,
            title: title,
            contentText: message,
            translationParams: translationParams,
            buttons: [{
                label: "woa.button.common.ok",
                result: (checkboxState: { [name: string]: boolean }) => checkboxState["acknowledged"]
            }],
            checkboxes: [{
                label: checkboxLabel,
                name: "acknowledged"
            }]
        });
        this.alertDisplayed$.next(false);
        return result === undefined ? cancelResult : result;
    }

    async displayConfirmation(title: string, subtitle: string, message: string, cancelLabel: string, confirmLabel: string, reverseConnotations: boolean = false, translationParams?: any, cancelResult: boolean = false, checkboxes?: CheckboxSpec[], useHistory: boolean = true): Promise<boolean> {
        this.alertDisplayed$.next(true);
        const result: any = await this.navigationService.createPage("woa-simple-dialog-page", {
            showCloseButton: false,
            title: title,
            subtitle: subtitle,
            contentText: message,
            popHistoryOnDismissal: useHistory,
            translationParams: translationParams,
            buttons: [{
                label: cancelLabel,
                connotation: reverseConnotations ? ButtonConnotation.AFFIRMATIVE : ButtonConnotation.NEGATIVE,
                result: false
            }, {
                label: confirmLabel,
                connotation: reverseConnotations ? ButtonConnotation.NEGATIVE : ButtonConnotation.AFFIRMATIVE,
                result: true
            }],
            checkboxes
        }, undefined, false, false, useHistory);
        this.alertDisplayed$.next(false);
        return result === undefined ? cancelResult : result;
    }

    logErrorToServer(message: string, context: any, error: any): void {
        message += ". Caused by '" + error.constructor.name + "' (see error).";
        if (error instanceof DataClientErrorMessage) {
            this.serverLoggingService.log(message, context, error);
        } else {
            try {
                this.serverLoggingService.log(message, context, JSON.stringify(error, Object.getOwnPropertyNames(error)));
            } catch (e) {
                this.serverLoggingService.log(message + " json unavailable.", context, error);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/notification/notification.service.ts